import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './theme/tokens.css';
import { ready as i18nReady } from './i18n';
import App from './App';
import ErrorBoundary from './components/ErrorBoundary';
import reportWebVitals from './reportWebVitals';
import { IS_NATIVE } from './utils/platform';
import { applyTheme, readStoredPreference } from './theme/themeStore';

// Before the first render, so a returning user never sees a frame of the wrong theme.
applyTheme(readStoredPreference());

const root = ReactDOM.createRoot(document.getElementById('root'));

/*
 * The first render waits for the active language's catalogues.
 *
 * Task 4.3b made them a lazy chunk, so they are no longer in hand the moment
 * this module runs. Rendering first and letting Suspense cover the gap would
 * show the app's raw translation keys for a frame -- `nav.collections`,
 * `login.submit` -- which is worse than the blank moment it replaces. The
 * chunk is local and precached, so this is a module resolution, not a
 * download, on every visit after the first.
 *
 * A LATER changeLanguage is different: the app is on screen by then, and the
 * Suspense boundary below is what covers it.
 *
 * `.finally`, not `.then`: a catalogue that fails to load must still get an
 * app -- in the reader's second language, or as keys -- rather than a page
 * that never renders at all.
 */
i18nReady.finally(() => {
  root.render(
    // ErrorBoundary is outside App, and therefore outside the router and
    // every provider: a throw in ThemeProvider or in a route element must
    // still land somewhere that can render, and anything inside App would go
    // down with it. Suspense is inside it, because a chunk that fails to
    // load should reach the error screen rather than hang.
    <React.StrictMode>
      <ErrorBoundary>
        <Suspense fallback={null}>
          <App />
        </Suspense>
      </ErrorBoundary>
    </React.StrictMode>
  );
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// The application shell, offline -- see public/service-worker.js for what it
// caches and, more importantly, what it must never cache.
//
// Production only: a worker under `npm start` serves yesterday's bundle over
// today's edits. And never inside a native shell, where the bundle is already
// on the device and a second cache can only serve a stale shell after an
// app-store update has replaced it.
if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator && !IS_NATIVE) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register(`${process.env.PUBLIC_URL}/service-worker.js`).catch(() => {
      // A browser that refuses -- private mode, or plain http on a LAN address
      // that is not localhost -- simply runs online-only, exactly as before.
    });
  });
}
